<script setup>
import { computed, watch, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useContractsStore } from '@/stores/contract.js'
import {useToast} from "primevue";
import Card from 'primevue/card';
import SpeedDial from 'primevue/speeddial';
import { fancyDate } from "@/filters/fancyDate.js";
import Contacts from "@/components/Contacts.vue";
import Works from "@/components/work/WorksForCard.vue";
import {useLoadByRouteId} from "@/composables/useLoadByRouteId.js";
import { useResponsiblesStore } from '@/stores/responsibles.js'
import ResponsibilityList from '@/components/common/ResponsibilityList.vue'
import WorkflowStatus from "@/components/common/WorkflowStatus.vue";
import CreateReminderDialog from "@/components/common/CreateReminderDialog.vue";
import EntityMessageDialog from "@/components/messages/EntityMessageDialog.vue";
import TaskCreateDialog from "@/components/tasks/TaskCreateDialog.vue";
import ContractForm from "@/components/contract/ContractForm.vue";
import ConfirmArchiveDialog from "@/components/common/ConfirmArchiveDialog.vue";
import ConfirmDeleteDialog from "@/components/common/ConfirmDeleteDialog.vue";
import EventHistoryDialog from "@/components/common/EventHistoryDialog.vue";
import { usePermissions } from '@/composables/usePermissions.js';


const route = useRoute()
const router = useRouter()
const contractsStore = useContractsStore()
const responsiblesStore = useResponsiblesStore()
const toast = useToast();
const { hasPermission } = usePermissions();

const contractId = computed(() => {
  const raw = route.params.id
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
})

watch(contractId, (id) => {
  if (id) responsiblesStore.loadCurrent('contract', id)
}, { immediate: true })

const showEditDialog = ref(false)
const showMessageDialog = ref(false)
const showConfirm = ref(false)
const showConfirmArchive = ref(false)
const showConfirmUnarchive = ref(false)
const showCreateTask = ref(false)
const showEventHistory = ref(false)


const contract = computed(() => contractsStore.contract)
const contractResponsiblesRaw = computed(() => responsiblesStore.items('contract', contractId.value))
const isArchived = computed(() => contract.value?.status === 'ARCHIVED')
const responsible = computed(() => {
  const list = contractResponsiblesRaw.value || []
  if (!Array.isArray(list) || list.length === 0) {
    return { id: null, name: '' }
  }
  const item = list.find(r => r?.is_primary) ?? list[0]
  const u = item?.user
  const id = Number(u?.id)
  const name = u?.full_name
      || [u?.last_name, u?.first_name].filter(Boolean).join(' ')
      || u?.name
      || ''
  return { id: Number.isFinite(id) && id > 0 ? id : null, name }
})


function openResponsibleMessage() {
  if (!responsible.value?.id) {
    toast.add({ severity: 'warn', summary: 'Не указан ответственный', life: 2000 })
    return
  }
  showMessageDialog.value = true
}

const customerId = computed(() => contract.value?.customer_id ?? null)
const facilityId = computed(() => contract.value?.facility?.id ?? contract.value?.facility_id ?? null)

useLoadByRouteId((id) => contractsStore.loadContract(id))

const showCreateReminder = ref(false)

function onContractUpdated() {
  if (contractId.value) {
    contractsStore.loadContract(contractId.value)
  }
}

async function onArchiveConfirm() {
  try {
    await contractsStore.archiveContract(route.params.id);
    toast.add({
      severity: 'success',
      summary: 'Договор архивирован',
      detail: 'Договор перемещён в архив',
      life: 2500,
    });
    showConfirm.value = false;
    await router.push('/contracts');
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось архивировать договор',
      life: 4000,
    });
  }
}

async function onUnarchiveConfirm() {
  try {
    await contractsStore.unarchiveContract(route.params.id)
    toast.add({
      severity: 'success',
      summary: 'Договор возвращён из архива',
      detail: 'Статус договора изменён на ACTIVE',
      life: 2500,
    })
  } catch (e) {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось вернуть договор из архива',
      life: 4000,
    })
  }
}

const blockerLabels = {
  works: 'Работы',
  contracts: 'Договоры',
  contacts: 'Контакты',
  comments: 'Комментарии'
}

function formatBlockers(blockers) {
  if (!blockers || typeof blockers !== 'object') return ''
  return Object.entries(blockers)
      .map(([key, count]) => `${blockerLabels[key] || key} (${count})`)
      .join('\n')
}

async function onDeleteConfirm() {
  try {
    await contractsStore.deleteContract(route.params.id)
    toast.add({
      severity: 'success',
      summary: 'Договор удален',
      detail: 'Данные договора успешно удалены',
      life: 2500,
    });
    await router.push('/contracts')
  } catch (e) {
    if (e.response?.status === 409 && e.response?.data?.blockers) {
      const data = e.response.data
      const blockersText = formatBlockers(data.blockers)
      const detail = `Найдены связанные сущности.\n${blockersText}\n Используйте архивирование.`
      toast.add({
        severity: 'warn',
        summary: 'Удаление невозможно',
        detail,
        life: 6000,
      });
    } else {
      toast.add({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Не удалось удалить договор',
        life: 4000,
      });
    }
  }
}
const reminderTarget = computed(() => {
  if (!contractId.value) return null
  return { app: 'contract', model: 'contract', objectId: Number(contractId.value) }
})
const taskContext = computed(() => {
  if (!contractId.value) return null
  return { app: 'contract', model: 'contract', objectId: Number(contractId.value) }
})
const menuItems = computed(() => {
  const items = []

  // Удалить - требует contract.delete_contract
  if (hasPermission('contract.delete_contract')) {
    items.push({
      label: 'Удалить',
      icon: 'pi pi-trash',
      severity: 'danger',
      command: () => {
        showConfirm.value = true
      }
    })
  }

  // Архивировать/Вернуть из архива - требует contract.archive_contract
  if (hasPermission('contract.archive_contract')) {
    items.push({
      label: isArchived.value ? 'Вернуть из архива' : 'Архивировать',
      icon: isArchived.value ? 'pi pi-sun' : 'pi pi-moon',
      severity: isArchived.value ? 'success' : 'danger',
      command: () => {
        const currentStatus = isArchived.value
        if (currentStatus) {
          showConfirmUnarchive.value = true
        } else {
          showConfirmArchive.value = true
        }
      }
    })
  }

  // Написать ответственному - доступно всем
  items.push({
    label: 'Написать ответственному',
    icon: 'pi pi-envelope',
    command: openResponsibleMessage
  })

  // Создать напоминание - доступно всем
  items.push({
    label: 'Создать напоминание',
    icon: 'pi pi-bell',
    command: () => {
      showCreateReminder.value = true
    }
  })

  // Создать задачу - требует task.create_for_contract
  if (hasPermission('task.create_for_contract')) {
    items.push({
      label: 'Создать задачу',
      icon: 'pi pi-pen-to-square',
      command: () => { showCreateTask.value = true }
    })
  }

  // История - доступна всем
  items.push({
    label: 'История',
    icon: 'pi pi-history',
    command: () => {
      showEventHistory.value = true
    }
  })

  // Редактировать - требует contract.change_contract
  if (hasPermission('contract.change_contract')) {
    items.push({
      label: 'Редактировать',
      icon: 'pi pi-pencil',
      command: () => {
        showEditDialog.value = true
      }
    })
  }

  return items
});
</script>

<template>
  <div>
    <div class="flex flex-col gap-4">
      <Card>
        <template #title>
          <div class="flex justify-between gap-2 items-center relative">
              <span>
                Договор {{ contract?.number ? `№ ${contract.number}` : '' }}
                <template v-if="contract?.date">
                  от {{ fancyDate(contract.date, 'short') }}
                </template>
              </span>
            <div style="position: relative; width: 44px; height: 44px;">
              <SpeedDial
                  :model="menuItems"
                  :radius="140"
                  type="quarter-circle"
                  direction="down-left"
                  :style="{ position: 'absolute', left: 0, bottom: 0 }"
                  buttonClass="p-button-text p-button-secondary"
                  :showIcon="'pi pi-ellipsis-v'"
                  :hideIcon="'pi pi-times'"
              />
            </div>
          </div>
        </template>

        <template #content>
          <div class="space-y-4">
            <div class="flex">
              <div class="w-4/5 space-y-4">
                <div>
                  <span class="font-semibold">Заказчик: </span>
                  <template v-if="customerId">
                    <RouterLink :to="`/customers/${customerId}`">
                      {{ contract?.customer || '—' }}
                    </RouterLink>
                  </template>
                  <template v-else>
                    {{ contract?.customer || '—' }}
                  </template>
                </div>

                <div>
                  <span class="font-semibold">Объект: </span>
                  <template v-if="facilityId">
                    <RouterLink :to="`/facilities/${facilityId}`">
                      {{ contract?.facility?.name || '—' }}
                    </RouterLink>
                  </template>
                  <template v-else>
                    {{ contract?.facility?.name || '—' }}
                  </template>
                </div>

                <div>
                  <span class="font-semibold">Адрес: </span>
                  {{ contract?.facility?.address || '—' }}
                </div>

                <WorkflowStatus
                    v-if="contractId"
                    entity="contract"
                    :id="contractId"
                    size="small"
                    @changed="() => contractsStore.loadContract(route.params.id)"
                />


              </div>
              <div class="text-right w-1/5 text-sm" v-if="contract?.facility?.term_start || contract?.facility?.term_end">
                <p v-if="contract?.facility?.term_start">Начало работ</p>
                <p class="text-orange-400" v-if="contract?.facility?.term_start">{{ fancyDate(contract?.facility?.term_start, 'short') }}</p>
                <p class="mt-2" v-if="contract?.facility?.term_end">Завершение работ</p>
                <p class="text-lime-600" v-if="contract?.facility?.term_end">{{ fancyDate(contract?.facility?.term_end, 'short') }}</p>
              </div>

            </div>
            <ResponsibilityList
                :list="contractResponsiblesRaw"
                singularTitle="Ответственный"
                pluralTitle="Ответственные"
                :removable="true"
                entityType="contract"
                :entityId="contractId"
                :showPriority="false"
                :allowAdd="true"

            />
            <Works :contract-id="Number(route.params.id) " :contract-number="contract?.number || ''"/>
            <Contacts v-if="contract?.id" target="contract" :id="contract.id" />
          </div>
        </template>
      </Card>
    </div>
    <ContractForm
        v-model:visible="showEditDialog"
        :contract-id="contractId"
        @updated="onContractUpdated"
    />
    <CreateReminderDialog
        v-if="reminderTarget"
        v-model="showCreateReminder"
        :targetAppLabel="reminderTarget.app"
        :targetModel="reminderTarget.model"
        :targetObjectId="reminderTarget.objectId"
    />
    <ConfirmDeleteDialog
        v-model:visible="showConfirm"
        :contractName="contract?.number|| ''"
        @confirm="onDeleteConfirm"
    />
    <ConfirmArchiveDialog
        v-model:visible="showConfirmArchive"
        :contractName="contract?.number|| ''"
        @confirm="onArchiveConfirm"
    />
    <ConfirmArchiveDialog
        v-model:visible="showConfirmUnarchive"
        mode="unarchive"
        :contractName="contract?.number|| ''"
        @confirm="onUnarchiveConfirm"
    />
    <EntityMessageDialog
        v-model="showMessageDialog"
        v-if="contract"
        :user-id="responsible.id"
        :responsible-name="responsible.name || 'Ответственный'"
        entity-type="contract"
        :entity-id="contract?.id"
        :entity-title="contract?.number || contract?.name || `#${contract?.id}`"
        :entity-url="`/contracts/${contract?.id}`"
    />
    <TaskCreateDialog
        v-if="taskContext"
        v-model="showCreateTask"
        :contextAppLabel="taskContext.app"
        :contextModel="taskContext.model"
        :contextId="taskContext.objectId"
        :defaultAssigneeId="responsible.id"
        :contextTitle="contract?.number || contract?.name || `#${contractId}`"
        @created="() => {}"
    />
    <EventHistoryDialog
        v-model:visible="showEventHistory"
        target="contract"
    :entityId="contractId"
    :entityName="contract?.number"
    />
  </div>
</template>

<style scoped>
:deep(.p-card-body) {
  padding-top: 0;
  padding-bottom: 8px;
}
</style>